export type GameAudioEvent =
  | 'fill'
  | 'spray'
  | 'empty'
  | 'pickup'
  | 'throw'
  | 'eat'
  | 'damage'
  | 'bloom'
  | 'collapse'
  | 'restart';

export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private duck = 1;
  private rng: () => number = () => 0.5;
  private readonly unlockListener = () => void this.unlock();

  constructor() {
    window.addEventListener('pointerdown', this.unlockListener);
    window.addEventListener('keydown', this.unlockListener);
  }

  setRandomSource(rng: () => number): void {
    this.rng = rng;
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.context = new AudioContextClass();
    await this.context.resume();
    this.unlocked = true;
    window.removeEventListener('pointerdown', this.unlockListener);
    window.removeEventListener('keydown', this.unlockListener);
  }

  play(event: GameAudioEvent, strength = 1): void {
    switch (event) {
      case 'fill':
        this.tone(190, 380, 0.24, 'sine', 0.07 * strength);
        break;
      case 'spray':
        this.noiseBurst(0.18, 900, 0.055 * strength);
        this.tone(520, 260, 0.14, 'sine', 0.025 * strength);
        break;
      case 'empty':
        this.tone(150, 110, 0.13, 'square', 0.025);
        break;
      case 'pickup':
        this.tone(310, 520, 0.16, 'triangle', 0.065 * strength);
        break;
      case 'throw':
        this.tone(170, 90, 0.18, 'sine', 0.075 * strength);
        break;
      case 'eat':
        this.tone(240, 340, 0.11, 'triangle', 0.055);
        this.tone(330, 470, 0.16, 'sine', 0.04, 0.07);
        break;
      case 'damage':
        this.noiseBurst(0.14, 360, 0.08 * strength);
        this.tone(120, 72, 0.2, 'sawtooth', 0.05);
        break;
      case 'bloom':
        this.tone(330, 660, 0.32, 'sine', 0.065);
        this.tone(440, 880, 0.36, 'triangle', 0.045, 0.08);
        break;
      case 'collapse':
        this.tone(115, 46, 0.7, 'sawtooth', 0.075);
        break;
      case 'restart':
        this.tone(220, 440, 0.22, 'triangle', 0.055);
        break;
    }
  }

  pickup(index: number): void {
    this.tone(300 + index * 16, 610 + index * 12, 0.18, 'triangle', 0.065);
  }

  setDuck(value: number): void {
    this.duck = Math.max(0, Math.min(1, value));
  }

  dispose(): void {
    window.removeEventListener('pointerdown', this.unlockListener);
    window.removeEventListener('keydown', this.unlockListener);
    void this.context?.close();
    this.context = null;
    this.unlocked = false;
  }

  private tone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    delay = 0,
  ): void {
    if (!this.context || this.context.state !== 'running') return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const now = this.context.currentTime + delay;
    const variance = 1 + (this.rng() - 0.5) * 0.08;
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, startFrequency * variance), now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency * variance), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume * this.duck), now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private noiseBurst(duration: number, lowpassFrequency: number, volume: number): void {
    if (!this.context || this.context.state !== 'running') return;
    const sampleRate = this.context.sampleRate;
    const buffer = this.context.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = this.rng() * 2 - 1;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    const now = this.context.currentTime;
    filter.type = 'lowpass';
    filter.frequency.value = lowpassFrequency;
    gain.gain.setValueAtTime(volume * this.duck, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.buffer = buffer;
    source.connect(filter).connect(gain).connect(this.context.destination);
    source.start(now);
  }
}
